import { useState, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { NotificationList } from '@/components/notifications/NotificationList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ViewTabs } from '@/components/ui/view-tabs';
import { useNotifications, type Notification, type NotificationListResponse } from '@/hooks/useNotifications';
import { Search, Loader2 } from 'lucide-react';

type Tab = 'active' | 'snoozed' | 'history';

export function NotificationsPage() {
  const [tab, setTab] = useState<Tab>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const { markAllRead, unreadCount } = useNotifications();

  // Fetch notifications with infinite scroll pagination
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['notifications', 'page', tab],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        tab,
        limit: '25',
        ...(pageParam ? { cursor: pageParam } : {}),
      });
      const result = await api.get(`/api/notifications?${params}`) as NotificationListResponse;
      return result;
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    initialPageParam: undefined as string | undefined,
  });

  // Flatten pages into single notifications array
  const notifications = data?.pages.flatMap((page) => page.notifications) || [];

  // Client-side search filter (search is applied locally for simplicity)
  const filteredNotifications = searchQuery
    ? notifications.filter(
        (n: Notification) =>
          n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          n.message.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : notifications;

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const getEmptyMessage = () => {
    switch (tab) {
      case 'active':
        return 'No active notifications';
      case 'snoozed':
        return 'No snoozed notifications';
      case 'history':
        return 'No notification history';
    }
  };

  const tabViews = [
    { value: 'active', label: 'Active', count: tab === 'active' ? unreadCount : undefined },
    { value: 'snoozed', label: 'Snoozed' },
    { value: 'history', label: 'History' },
  ];

  return (
    <DashboardLayout title="Notifications">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1" />
          {tab === 'active' && unreadCount > 0 && (
            <Button variant="outline" onClick={() => markAllRead()}>
              Mark all as read
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Tabs */}
        <ViewTabs
          views={tabViews}
          currentView={tab}
          onChange={(v) => setTab(v as Tab)}
          className="mb-4"
        />

        {/* Content */}
        <div className="border rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading notifications...
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {searchQuery ? 'No matching notifications' : getEmptyMessage()}
            </div>
          ) : (
            <NotificationList
              notifications={filteredNotifications}
              showSnoozeActions={tab === 'active'}
              showUnsnoozeAction={tab === 'snoozed'}
            />
          )}
        </div>

        {/* Load more button */}
        {hasNextPage && !searchQuery && (
          <div className="mt-4 text-center">
            <Button
              variant="outline"
              onClick={handleLoadMore}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                'Load more'
              )}
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
